// client/src/pages/admin/ConciergeOnboardingDetail.js
// ============================================
// CONCIERGE ONBOARDING — BATCH DETAIL
// View + edit a draft batch's records; process batch; resend
// invites; live-polls while status === 'processing'.
// ============================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Play, Trash2, Edit2, Send, Plus } from 'lucide-react';
import { adminOnboardingAPI } from '../../services/api';
import BatchRecordForm from '../../components/onboarding/BatchRecordForm';
import ConfirmModal from '../../components/common/ConfirmModal';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const statusColor = {
  pending: 'bg-gray-100 text-gray-700',
  created: 'bg-blue-100 text-blue-700',
  invited: 'bg-green-100 text-green-700',
  skipped_duplicate: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-700'
};

const batchStatusColor = {
  draft: 'bg-gray-100 text-gray-700',
  processing: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  partially_failed: 'bg-amber-100 text-amber-800'
};

const ConciergeOnboardingDetail = () => {
  const { t } = useTranslation('admin');
  const { batchId } = useParams();
  const navigate = useNavigate();

  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await adminOnboardingAPI.getBatch(batchId);
      const b = res.data?.batch || res.data?.data || res.data;
      setBatch(b);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load batch');
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (batch?.status === 'processing') {
      pollRef.current = setInterval(load, 3000);
      return () => clearInterval(pollRef.current);
    }
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [batch?.status, load]);

  const isDraft = batch?.status === 'draft';
  const records = batch?.records || [];

  const existingEmails = useMemo(
    () => records.filter((r) => r._id !== editingRecordId).map((r) => r.email),
    [records, editingRecordId]
  );

  const handleAppend = async (record) => {
    try {
      await adminOnboardingAPI.appendRecord(batchId, record);
      toast.success(t('conciergeOnboarding.toasts.recordAdded', 'Record added'));
      setShowAddForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add record');
    }
  };

  const handleUpdate = async (record) => {
    try {
      await adminOnboardingAPI.updateRecord(batchId, editingRecordId, record);
      toast.success(t('conciergeOnboarding.toasts.recordUpdated', 'Record updated'));
      setEditingRecordId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update record');
    }
  };

  const handleRemove = (record) => {
    setConfirm({
      title: t('conciergeOnboarding.confirm.removeRecord.title', 'Remove record?'),
      message: record.businessName,
      onConfirm: async () => {
        try {
          await adminOnboardingAPI.removeRecord(batchId, record._id);
          toast.success(t('conciergeOnboarding.toasts.recordRemoved', 'Record removed'));
          setConfirm(null);
          load();
        } catch (err) {
          toast.error(err.response?.data?.message || 'Failed to remove record');
        }
      }
    });
  };

  const handleProcess = () => {
    setConfirm({
      title: t('conciergeOnboarding.confirm.processBatch.title', 'Create accounts & send invites?'),
      message: t(
        'conciergeOnboarding.confirm.processBatch.message',
        'This will create the business accounts and email each one a temporary password. Processed accounts cannot be undone.'
      ),
      icon: 'warning',
      confirmColor: 'blue',
      onConfirm: async () => {
        try {
          setConfirm(null);
          await adminOnboardingAPI.processBatch(batchId);
          toast.success(t('conciergeOnboarding.toasts.processing', 'Processing batch…'));
          load();
        } catch (err) {
          toast.error(err.response?.data?.message || 'Failed to process batch');
        }
      }
    });
  };

  const handleResend = async (record) => {
    try {
      await adminOnboardingAPI.resendInvite(batchId, record._id);
      toast.success(t('conciergeOnboarding.toasts.resent', 'Invitation resent'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resend');
    }
  };

  const handleTogglePref = async (partial) => {
    try {
      const res = await adminOnboardingAPI.updateBatch(batchId, partial);
      const updated = res.data?.batch || res.data?.data || res.data;
      setBatch(updated);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update batch');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  if (!batch) {
    return (
      <div className="p-8 text-center text-gray-600">
        {t('conciergeOnboarding.notFound', 'Batch not found.')}
      </div>
    );
  }

  const wsName =
    batch.wholesaler?.companyName ||
    batch.wholesaler?.wholesalerName ||
    batch.wholesaler?.email ||
    '—';

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <button
              onClick={() => navigate('/admin/onboarding')}
              className="text-sm text-indigo-600 hover:text-indigo-800 mb-2"
            >
              ← {t('conciergeOnboarding.backToList', 'Back to batches')}
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('conciergeOnboarding.detailTitle', 'Batch for {{name}}', { name: wsName })}
            </h1>
            <div className="mt-2 flex items-center gap-2">
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${batchStatusColor[batch.status] || 'bg-gray-100'}`}>
                {t(`conciergeOnboarding.statusChips.${batch.status}`, batch.status)}
              </span>
              <span className="text-xs text-gray-500">
                {records.length} {t('conciergeOnboarding.records', 'records')}
              </span>
            </div>
          </div>

          {isDraft && (
            <button
              onClick={handleProcess}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 text-white px-4 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              disabled={records.length === 0}
            >
              <Play className="w-4 h-4" />
              {t('conciergeOnboarding.process', 'Create accounts & send invites')}
            </button>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={Boolean(batch.notifyWholesaler)}
                onChange={(e) => handleTogglePref({ notifyWholesaler: e.target.checked })}
                disabled={!isDraft}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              {t('conciergeOnboarding.toggles.notifyWholesaler', 'Notify wholesaler when complete')}
            </label>

            <fieldset className="flex items-center gap-4" disabled={!isDraft}>
              <legend className="text-sm font-medium text-gray-700 mr-2">
                {t('conciergeOnboarding.form.labels.emailBranding', 'Email branding')}
              </legend>
              <label className="inline-flex items-center gap-1 text-sm">
                <input
                  type="radio"
                  name="emailBranding"
                  checked={batch.emailBranding === 'wholesaler'}
                  onChange={() => handleTogglePref({ emailBranding: 'wholesaler' })}
                />
                {t('conciergeOnboarding.toggles.emailBrandingWholesaler', 'Wholesaler logo')}
              </label>
              <label className="inline-flex items-center gap-1 text-sm">
                <input
                  type="radio"
                  name="emailBranding"
                  checked={batch.emailBranding === 'platform'}
                  onChange={() => handleTogglePref({ emailBranding: 'platform' })}
                />
                {t('conciergeOnboarding.toggles.emailBrandingPlatform', 'WholesaleHub logo')}
              </label>
            </fieldset>
          </div>
          {batch.note && (
            <p className="text-sm text-gray-600 border-l-2 border-gray-200 pl-3 italic">
              {batch.note}
            </p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('conciergeOnboarding.form.recordsSectionTitle', 'Businesses to onboard')}
            </h2>
            {isDraft && !showAddForm && editingRecordId == null && (
              <button
                onClick={() => setShowAddForm(true)}
                className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800"
              >
                <Plus className="w-4 h-4" />
                {t('conciergeOnboarding.form.addRecord', 'Add record')}
              </button>
            )}
          </div>

          {records.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">
              {t('conciergeOnboarding.empty.records', 'No records yet.')}
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">{t('conciergeOnboarding.form.labels.businessName', 'Business')}</th>
                  <th className="px-3 py-2 text-left">{t('conciergeOnboarding.form.labels.email', 'Email')}</th>
                  <th className="px-3 py-2 text-left">{t('conciergeOnboarding.form.labels.vatNumber', 'VAT')}</th>
                  <th className="px-3 py-2 text-left">{t('conciergeOnboarding.listColumns.status', 'Status')}</th>
                  <th className="px-3 py-2 text-right">{t('conciergeOnboarding.listColumns.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {records.map((r) => (
                  <React.Fragment key={r._id}>
                    <tr>
                      <td className="px-3 py-2">{r.businessName}</td>
                      <td className="px-3 py-2">{r.email}</td>
                      <td className="px-3 py-2">{r.vatNumber}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${statusColor[r.status] || 'bg-gray-100'}`}>
                          {t(`conciergeOnboarding.recordStatus.${r.status}`, r.status)}
                        </span>
                        {r.failureReason && (
                          <p className="mt-1 text-xs text-red-600">{r.failureReason}</p>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {isDraft && (
                          <>
                            <button
                              onClick={() => setEditingRecordId(r._id)}
                              className="inline-flex items-center text-indigo-600 hover:text-indigo-800 mr-3"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRemove(r)}
                              className="inline-flex items-center text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {!isDraft && r.status === 'created' && (
                          <button
                            onClick={() => handleResend(r)}
                            className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800"
                          >
                            <Send className="w-4 h-4" />
                            {t('conciergeOnboarding.resendInvite', 'Resend')}
                          </button>
                        )}
                      </td>
                    </tr>
                    {editingRecordId === r._id && (
                      <tr>
                        <td colSpan={5} className="p-4 bg-gray-50">
                          <BatchRecordForm
                            initialValue={r}
                            onSubmit={handleUpdate}
                            onCancel={() => setEditingRecordId(null)}
                            submitLabel={t('conciergeOnboarding.form.updateRecord', 'Update record')}
                            existingEmails={existingEmails}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}

          {showAddForm && isDraft && (
            <div className="p-4 border-t bg-gray-50">
              <BatchRecordForm
                onSubmit={handleAppend}
                onCancel={() => setShowAddForm(false)}
                submitLabel={t('conciergeOnboarding.form.addRecord', 'Add record')}
                existingEmails={records.map((r) => r.email)}
              />
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        icon={confirm?.icon || 'danger'}
        confirmColor={confirm?.confirmColor || 'red'}
        onConfirm={confirm?.onConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

export default ConciergeOnboardingDetail;
